import {
  ID,
  newTaskSuffix,
  noDeadlineSectionID,
  overdueSectionID,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  TaskType,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { addDays, endOfDay, isAfter, isBefore } from 'date-fns';

import { formatDateKey, parseDateKey } from '../format-date-key';
import { getNextOccurrence } from '../get-next-occurrence';
import { getSortedTasks } from '../get-sorted-tasks';
import { getDueTaskPlanning } from '../get-task-planning';

export type BuildDeadlineTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyOverdue?: boolean;
  showEmptyNoDate?: boolean;
  showEmptyDate?: boolean;
  dateFilterEndDate?: Timestamp | null;
  hideNewTaskRow?: boolean;
  overdueLabel: string;
  noDeadlineLabel: string;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyOverdue,
  showEmptyNoDate,
  showEmptyDate,
  dateFilterEndDate,
  hideNewTaskRow,
  overdueLabel,
  noDeadlineLabel,
  rootLabel,
  newTaskLabel,
}: BuildDeadlineTreeOptions): Record<ID, TaskListTreeItem> => {
  const overdueDeadline = endOfDay(addDays(today, -1));
  const dateSections: Record<string, { date: Timestamp; taskIds: ID[] }> = {};
  const overdueTaskIds: ID[] = [];
  const overdueTasks: Task[] = [];
  const standardTasksMap = new Map<ID, Task>();
  const noDeadlineTaskIds = new Set<ID>();

  tasks.forEach((task) => {
    const effectiveDeadline =
      !task.schedules?.length &&
      (task.endStrategy?.deadline || task.deadlineDeadline)
        ? new Date(
            Math.min(
              ...([
                task.endStrategy?.deadline?.getTime(),
                task.deadlineDeadline?.getTime(),
              ].filter(
                (timestamp) => typeof timestamp === 'number',
              ) as number[]),
            ),
          )
        : null;

    const overduePlanning = getDueTaskPlanning(task, {
      date: overdueDeadline,
      today,
      weekStartsOn,
    });

    const duePlanning = overduePlanning.filter(
      (plannedOccurrence) => !isAfter(plannedOccurrence.date, overdueDeadline),
    );

    const plannedOccurrence = duePlanning[duePlanning.length - 1];
    const isOverdue =
      !!plannedOccurrence ||
      (!!effectiveDeadline && !isAfter(effectiveDeadline, overdueDeadline));

    const planning = effectiveDeadline
      ? []
      : getDueTaskPlanning(task, {
          date: today,
          today,
          weekStartsOn,
        });

    if (!planning.length && !effectiveDeadline) {
      if (!task.schedules?.length) {
        noDeadlineTaskIds.add(task.id);
        standardTasksMap.set(task.id, task);
        return;
      }
    }

    if (isOverdue) {
      overdueTaskIds.push(task.id);
      overdueTasks.push(task);
    }

    const taskDates = new Set<string>();

    planning.forEach(({ date }) => {
      if (!isBefore(date, today)) {
        if (
          dateFilterEndDate &&
          isAfter(date, dateFilterEndDate) &&
          task.type !== TaskType.Task
        ) {
          return;
        }

        const dateKey = formatDateKey(date);
        taskDates.add(dateKey);
      }
    });

    if (effectiveDeadline && !isBefore(effectiveDeadline, today)) {
      const shouldSkip =
        dateFilterEndDate &&
        isAfter(effectiveDeadline, dateFilterEndDate) &&
        task.type !== TaskType.Task;

      if (!shouldSkip) {
        const dateKey = formatDateKey(effectiveDeadline);
        taskDates.add(dateKey);
      }
    }

    if (!taskDates.size && task.schedules?.length) {
      const nextOccurrence = getNextOccurrence(task, {
        today,
        weekStartsOn,
      })?.date;

      if (
        nextOccurrence &&
        !(dateFilterEndDate && isAfter(nextOccurrence, dateFilterEndDate))
      ) {
        taskDates.add(formatDateKey(nextOccurrence));
      }

      if (!taskDates.size && !isOverdue) {
        noDeadlineTaskIds.add(task.id);
        standardTasksMap.set(task.id, task);
        return;
      }
    }

    taskDates.forEach((dateKey) => {
      if (!dateSections[dateKey]) {
        dateSections[dateKey] = {
          date: new Date(dateKey),
          taskIds: [],
        };
      }
      dateSections[dateKey].taskIds.push(task.id);
      standardTasksMap.set(task.id, task);
    });
  });

  if (showEmptyDate) {
    const todayKey = formatDateKey(today);
    if (!dateSections[todayKey]) {
      dateSections[todayKey] = {
        date: today,
        taskIds: [],
      };
    }
  }

  const dateTreeItems: Record<ID, TaskListTreeItem> = {};

  if (overdueTaskIds.length || showEmptyOverdue) {
    dateTreeItems[overdueSectionID] = {
      id: overdueSectionID,
      type: TaskListItemTypes.Date,
      name: overdueLabel,
      children: [],
      isFolder: true,
    };
  }

  const sortedDateKeys = Object.keys(dateSections).sort(
    (a, b) => dateSections[a].date.getTime() - dateSections[b].date.getTime(),
  );

  sortedDateKeys.forEach((dateKey) => {
    dateTreeItems[dateKey] = {
      id: dateKey,
      type: TaskListItemTypes.Date,
      name: dateKey,
      children: [],
      isFolder: true,
    };
  });

  if (noDeadlineTaskIds.size || showEmptyNoDate) {
    dateTreeItems[noDeadlineSectionID] = {
      id: noDeadlineSectionID,
      type: TaskListItemTypes.Date,
      name: noDeadlineLabel,
      children: [],
      isFolder: true,
    };
  }

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? Object.keys(dateTreeItems).reduce<ID[]>((acc, sectionId) => {
          const sectionOrder = customOrder?.[sectionId] ?? [];
          acc.push(...sectionOrder);
          return acc;
        }, [])
      : [];

  const overdueSortedTasks = getSortedTasks(overdueTasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: [SortingMode.DueDate],
    invertedTimeslots: true,
  });

  overdueSortedTasks.forEach((task) => {
    if (dateTreeItems[overdueSectionID]) {
      dateTreeItems[overdueSectionID].children?.push(task.id);
    }

    dateTreeItems[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
  });

  const registerTaskTreeItem = (task: Task) => {
    dateTreeItems[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
  };

  const tasksByIds = (ids: Iterable<ID>) =>
    Array.from(ids)
      .map((id) => standardTasksMap.get(id))
      .filter((task): task is Task => !!task);

  // No-deadline tasks carry no schedule occurrence, so their order comes from
  // task.timeslot / createdAt — there is no section date to resolve against.
  const noDeadlineSortedTasks = getSortedTasks(tasksByIds(noDeadlineTaskIds), {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  });

  noDeadlineSortedTasks.forEach((task) => {
    if (dateTreeItems[noDeadlineSectionID]) {
      dateTreeItems[noDeadlineSectionID].children?.push(task.id);
    }
    registerTaskTreeItem(task);
  });

  sortedDateKeys.forEach((dateKey) => {
    // parseDateKey keeps the day local — new Date(key) parses as UTC and drifts a day west.
    const sectionDate = parseDateKey(dateKey);
    const sectionSortedTasks = getSortedTasks(
      tasksByIds(dateSections[dateKey].taskIds),
      {
        today,
        weekStartsOn,
        order: customTaskOrder,
        mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
        dateRange: { startDate: sectionDate, endDate: endOfDay(sectionDate) },
      },
    );

    sectionSortedTasks.forEach((task) => {
      if (dateTreeItems[dateKey]) {
        dateTreeItems[dateKey].children?.push(task.id);
      }
      registerTaskTreeItem(task);
    });
  });

  const allTreeItems = dateTreeItems;

  if (!hideNewTaskRow) {
    Object.keys(dateTreeItems).forEach((dateKey) => {
      if (
        dateKey === overdueSectionID ||
        dateKey === noDeadlineSectionID ||
        !dateTreeItems[dateKey].children
      ) {
        return;
      }
      const section = dateTreeItems[dateKey];
      if (section) {
        const newTaskItemId = `${dateKey}${newTaskSuffix}`;
        section.children?.push(newTaskItemId);
        allTreeItems[newTaskItemId] = {
          id: newTaskItemId,
          name: newTaskLabel,
          type: TaskListItemTypes.NewTask,
        };
      }
    });

    if (dateTreeItems[noDeadlineSectionID]) {
      const newTaskItemId = `${noDeadlineSectionID}${newTaskSuffix}`;
      dateTreeItems[noDeadlineSectionID].children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    }
  }

  const sectionIds: ID[] = [];
  if (dateTreeItems[overdueSectionID]) {
    sectionIds.push(overdueSectionID);
  }
  sectionIds.push(...sortedDateKeys);
  if (dateTreeItems[noDeadlineSectionID]) {
    sectionIds.push(noDeadlineSectionID);
  }

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: sectionIds,
    },
    ...allTreeItems,
  };
};
